import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import { useCommunity } from '@/context/CommunityContext';

interface ServerRule {
  title: string;
  icon: string;
  description: string;
  punishment: string;
}

// Killing Floor-specific rules ONLY. The universal Uliunai code of conduct
// (Be Respectful / Fair Play / Keep Chat Clean) is owned by the hub
// (uliunai.com/conduct) and is intentionally NOT repeated here — every rule
// below is a KF-specific mechanic, exploit, punishment or policy.
const serverRules: ServerRule[] = [
  {
    title: 'Perk Exploiting',
    icon: '⚠️',
    description: 'Exploiting server bugs or maps to boost your own or others\' perks.',
    punishment: 'Perk levels will be removed down to 0 — we support fair play.',
  },
  {
    title: 'Griefing',
    icon: '\u{1F6B7}',
    description: 'Intentionally creating obstacles for other players — blocking paths, welding teammates in, or sabotaging the team.',
    punishment: 'Session kick — the player cannot reconnect until the map is over. Consistent griefing will result in a days-long or permanent ban.',
  },
  {
    title: 'Team Damage & Team Killing',
    icon: '⚔️',
    description: 'Dealing damage or killing players on your own team.',
    punishment: 'Session kick — the player cannot reconnect until the map is over. Consistent offenses will result in a days-long or permanent ban.',
  },
  {
    title: 'Inappropriate Nicknames',
    icon: '\u{1F3AE}',
    description: 'Inappropriate, advertising, or default nicknames. You can change the default nickname in Steam settings or in the rev.ini file for pirate game versions.',
    punishment: 'Nickname length: 3–20 characters. Allowed characters: a-Z 0-9 !,.-_^*$@#(){}[]|<>+',
  },
  {
    title: 'Votekick Abuse',
    icon: '❌',
    description: 'Unreasonable votes for the kick of other players.',
    punishment: 'Session kick if done consistently. Repeated abuse will lead to a days-long or permanent ban.',
  },
];

export default function RulesPage() {
  const { discordInvite } = useCommunity();
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Navigation />
      <main className="relative z-10 pt-20 pb-16 flex-1">
        <div className="container mx-auto px-4">
          <Link to="/" className="text-gray-400 hover:text-red-400 transition-colors text-sm mb-8 inline-block">
            <i className="ri-arrow-left-s-line"></i> Back to Home
          </Link>

          <header className="max-w-4xl mb-12">
            <p className="text-red-400 font-bold tracking-[0.2em] uppercase text-sm mb-3">Community Code</p>
            <h1 className="text-4xl md:text-5xl font-bold font-orbitron horror-heading mb-4">
              Server <span className="text-red-500">Rules</span>
            </h1>
            <div className="w-24 h-1 bg-red-600 mb-6"></div>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mb-6">
              Play fair, respect others, and keep the server fun for everyone. Breaking these rules can lead to mutes, session kicks, or permanent bans depending on severity.
            </p>
            <p className="text-gray-400 leading-relaxed max-w-3xl">
              These are the Killing Floor server rules. The universal{' '}
              <a
                href="https://uliunai.com/conduct"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-400 hover:text-red-300 transition-colors font-semibold"
              >
                community-wide code of conduct
              </a>{' '}
              applies across every Uliunai game and is maintained on the hub.
            </p>
          </header>

          <div className="grid gap-5 md:grid-cols-2 max-w-5xl mx-auto">
            {serverRules.map((rule, index) => (
              <Card key={rule.title} variant="dark" className="h-full hover:border-red-800/60 transition-colors duration-200">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-red-950/40 border border-red-900/50 flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-lg leading-none">{rule.icon}</span>
                    <span className="text-[10px] text-red-400 font-orbitron mt-1">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl md:text-2xl font-bold text-white font-orbitron mb-2">
                      {rule.title}
                    </h2>
                    <p className="text-gray-300 leading-relaxed mb-2">{rule.description}</p>
                    <p className="text-red-400/80 text-sm leading-relaxed">
                      <i className="ri-error-warning-line mr-1"></i>
                      {rule.punishment}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <section className="mt-14">
            <Card variant="blood" className="max-w-4xl mx-auto">
              <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
                <div>
                  <h2 className="text-2xl font-bold text-white font-orbitron mb-3">
                    Need help or want to report?
                  </h2>
                  <p className="text-gray-300 leading-relaxed">
                    Use the support ticket channel on Discord for reports, appeals, or questions. You can also DM an Admin if something needs a private answer.
                  </p>
                </div>
                <a
                  href={discordInvite}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-3 font-bold text-white transition-colors hover:bg-red-700 whitespace-nowrap"
                >
                  <i className="ri-discord-fill"></i>
                  Open Discord
                </a>
              </div>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
